import { useEffect, useRef, useState } from "react";
import {
  PaymentRequestButtonElement,
  useStripe,
} from "@stripe/react-stripe-js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useExam } from "../../../../contexts/examContext";
import { useSnackBar } from "../../../../contexts/snackbar";
import { createWalletIntent } from "../../../../lib/api";
import {
  QuickPaymentSection,
  QuickPaymentTitle,
  WalletSeparator,
} from "./styles";

export const WalletButton = () => {
  const stripe = useStripe();
  const [exam] = useExam();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const { t } = useTranslation();

  const [paymentRequest, setPaymentRequest] = useState(null);
  const paymentRequestRef = useRef(null);
  const mountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const intentPromiseRef = useRef(null);
  const paymentMethodSeenRef = useRef(false);
  const handlerRef = useRef(() => {});
  const cancelHandlerRef = useRef(() => {});

  const price = exam?.payment?.price;
  const amountInCents =
    typeof price === "number" && Number.isFinite(price) && price > 0
      ? Math.round(price * 100)
      : null;
  const totalLabel = t("payment.walletTotalLabel");

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!stripe || !amountInCents) return;

    if (paymentRequestRef.current) {
      paymentRequestRef.current.update({
        total: { label: totalLabel, amount: amountInCents },
      });
      return;
    }

    const request = stripe.paymentRequest({
      country: "BR",
      currency: "brl",
      total: { label: totalLabel, amount: amountInCents },
      disableWallets: ["link", "browserCard"],
    });

    let active = true;
    request
      .canMakePayment()
      .then((result) => {
        if (!active || !mountedRef.current) return;
        if (result?.applePay !== true && result?.googlePay !== true) return;
        paymentRequestRef.current = request;
        setPaymentRequest(request);
      })
      .catch(() => {});

    return () => {
      active = false;
    };
  }, [stripe, amountInCents, totalLabel]);

  const finishAttempt = () => {
    inFlightRef.current = false;
    intentPromiseRef.current = null;
  };

  const failAttempt = () => {
    finishAttempt();
    if (!mountedRef.current) return;
    showSnackBar(t("payment.errorToPay"), "error");
  };

  const handlePaymentMethod = async (event) => {
    paymentMethodSeenRef.current = true;
    const pendingIntent = intentPromiseRef.current;

    if (!pendingIntent) {
      event.complete("fail");
      return;
    }

    try {
      const response = await pendingIntent;
      const clientSecret = response?.data?.client_secret;

      if (!clientSecret) {
        event.complete("fail");
        failAttempt();
        return;
      }

      const { error } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: event.paymentMethod.id,
      });

      if (error) {
        event.complete("fail");
        failAttempt();
        return;
      }

      event.complete("success");
      finishAttempt();

      if (!mountedRef.current) return;
      navigate("/finish-purchase", {
        state: { id: "", payment_method: "wallet", value: price },
      });
    } catch {
      event.complete("fail");
      failAttempt();
    }
  };

  const handleCancel = () => {
    if (paymentMethodSeenRef.current) return;
    finishAttempt();
  };

  useEffect(() => {
    handlerRef.current = handlePaymentMethod;
    cancelHandlerRef.current = handleCancel;
  }, [handlePaymentMethod, handleCancel]);

  useEffect(() => {
    if (!paymentRequest) return;

    const listener = (event) => handlerRef.current(event);
    const cancelListener = () => cancelHandlerRef.current();
    paymentRequest.on("paymentmethod", listener);
    paymentRequest.on("cancel", cancelListener);

    return () => {
      paymentRequest.off("paymentmethod", listener);
      paymentRequest.off("cancel", cancelListener);
    };
  }, [paymentRequest]);

  const handleClick = () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    paymentMethodSeenRef.current = false;
    const intent = createWalletIntent();
    intent.catch(() => {});
    intentPromiseRef.current = intent;
  };

  if (!paymentRequest) return null;

  return (
    <QuickPaymentSection>
      <QuickPaymentTitle>{t("payment.quickPayment")}</QuickPaymentTitle>
      <PaymentRequestButtonElement
        options={{
          paymentRequest,
          style: {
            paymentRequestButton: {
              type: "default",
              theme: "dark",
              height: "48px",
            },
          },
        }}
        onClick={handleClick}
      />
      <WalletSeparator>{t("payment.orPayWith")}</WalletSeparator>
    </QuickPaymentSection>
  );
};
